import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useSidebar } from '@/stores/sidebar'
import { useMenuPath } from '@/composables/useMenuPath'
import { toTablerIconName } from '@/utils/icons'
import type { MenuGroup, MenuSubItem, MenuItem, SubItem } from '@/types/sidebar.d'

/** Sanitize API ids so they are safe as DOM ids (aria-controls) and v-for keys */
const safeId = (id: string): string => String(id).replace(/[^\w-]/g, '')

/**
 * Sidebar menu logic: normalizes API menu data for rendering, tracks the
 * active group/item per route, and owns the accordion + rail-collapse state.
 */
export function useSidebarMenu(storeId: string | number = 'main') {
  const route = useRoute()
  const sidebar = useSidebar(storeId)()
  const { isEditCampaignPath } = useMenuPath()

  // Normalized groups: icons resolved to tabler names, colors resolved per row.
  // Recomputes whenever the store replaces `items` (initialize / fetchMenu).
  const groups = computed<MenuGroup[]>(() =>
    (sidebar.items ?? []).map((group: MenuItem) => ({
      id: safeId(group.id),
      label: group.label,
      path: group.path,
      icon: toTablerIconName(group.icon),
      color: group.color || CONFIG.BASE_COLOR,
      items: group.items?.map((item: SubItem): MenuSubItem => ({
        id: safeId(item.id),
        label: item.label,
        path: item.path,
        color: item.color || group.color || CONFIG.BASE_COLOR,
      })),
    })),
  )

  // UI state
  const openGroups = ref<string[]>([])
  const activeGroupId = ref<string | null>(null)
  const activeItemPath = ref<string | null>(null)
  const collapsed = computed(() => sidebar.collapsed)

  const findActiveGroup = (path: string): MenuGroup | undefined => {
    if (isEditCampaignPath(path)) {
      return (
        groups.value.find((g) => g.items?.some((i) => i.path === '/campaigns/edit')) ||
        groups.value.find((g) => g.items?.some((i) => i.path === '/report/campaigns'))
      )
    }
    return groups.value.find((g) => g.path === path || g.items?.some((i) => i.path === path))
  }

  const updateActiveState = (path: string) => {
    let effectivePath = path
    if (isEditCampaignPath(path)) {
      const hasEdit = groups.value.some((g) => g.items?.some((i) => i.path === '/campaigns/edit'))
      effectivePath = hasEdit ? '/campaigns/edit' : '/report/campaigns'
    }

    activeItemPath.value = effectivePath
    const activeGroup = findActiveGroup(path)
    activeGroupId.value = activeGroup?.id ?? null

    // Auto-open the active group's accordion (single-open)
    if (activeGroup && !openGroups.value.includes(activeGroup.id)) {
      openGroups.value = [activeGroup.id]
    }
  }

  watch(
    () => route.path,
    (newPath) => updateActiveState(newPath),
    { immediate: true },
  )

  // Re-resolve when the menu data arrives (localStorage cache or API fetch)
  watch(groups, () => updateActiveState(route.path))

  const toggleCollapsed = () => sidebar.setCollapsed(!sidebar.collapsed)

  const handleGroupToggle = (group: MenuGroup) => {
    // In rail mode, expand the sidebar first, then open the group's accordion
    if (collapsed.value) {
      sidebar.setCollapsed(false)
      openGroups.value = [group.id]
      return
    }
    openGroups.value = openGroups.value.includes(group.id) ? [] : [group.id]
  }

  return {
    groups,
    openGroups,
    activeGroupId,
    activeItemPath,
    collapsed,
    toggleCollapsed,
    handleGroupToggle,
  }
}
